import React, { useEffect, useRef } from 'react';
import { Stack } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { isEqual } from 'lodash';
import { RecordField } from '../../types';
import { FieldInput } from '../../../../EmployeeData/Form/FieldInput';
import { FieldType } from '../../../../../__generated__/graphql';
import { FormatFieldValue } from '../../../../../manager/MyTeam/MyTeamChangeRequests/components/FormatFieldValue';
import { Form as CustomForm } from '../../../../../../../components/form/Form';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import { toISODateOnly } from '../../../../../../../utils/dateUtils';

type StepEmployeeDetailsComponentProps = {
  recordFields: Array<RecordField>;
  defaultValues: { [key: string]: string };
  onChange: (fieldData: { [key: string]: string }) => void;
  title?: string;
  description?: string;
};

const normalizeFieldValue = (
  value: unknown,
  fieldType: FieldType | undefined,
): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    if (fieldType === FieldType.Datetime) {
      return value.toISOString();
    }
    return toISODateOnly(value);
  }
  return String(value);
};

const getChangedFields = (
  currentValues: Record<string, unknown>,
  originalFields: Array<RecordField>,
): { [key: string]: string } => {
  const changedFields: { [key: string]: string } = {};

  for (const fieldName of Object.keys(currentValues)) {
    const originalField = originalFields.find(
      (field) => field.fieldInfo.name === fieldName,
    );
    const originalValue = originalField?.value || '';
    const fieldType = originalField?.fieldInfo.type;
    const newValue = normalizeFieldValue(currentValues[fieldName], fieldType);

    if (newValue !== originalValue) {
      changedFields[fieldName] = newValue;
    }
  }
  return changedFields;
};

/**
 * Watches react-hook-form values and propagates changes to the parent.
 * This captures changes from all input types including Datepicker,
 * which stores values directly in form state via Controller.
 */
const FormValuesWatcher: React.FC<
  React.PropsWithChildren<{
    recordFields: Array<RecordField>;
    onChange: (fieldData: { [key: string]: string }) => void;
    inputsMounted: boolean;
  }>
> = ({ recordFields, onChange, inputsMounted }) => {
  const { watch } = useFormContext();
  const watchedValues = watch();
  const prevChangedFieldsRef = useRef<{ [key: string]: string }>({});

  useEffect(() => {
    if (!inputsMounted) {
      return;
    }
    const changedFields = getChangedFields(watchedValues, recordFields);
    if (!isEqual(changedFields, prevChangedFieldsRef.current)) {
      prevChangedFieldsRef.current = changedFields;
      onChange(changedFields);
    }
  }, [watchedValues, recordFields, onChange, inputsMounted]);

  return null;
};

export const StepEmployeeDetailsComponent: React.FC<
  React.PropsWithChildren<StepEmployeeDetailsComponentProps>
> = ({ recordFields, defaultValues, onChange, title, description }) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const resolvedTitle = title ?? t('steps.employeeDetails');
  const resolvedDescription =
    description ?? t('steps.employee_detailsDescription');

  return (
    <Stack gap={0}>
      <div className="mb-4">
        <h4>{resolvedTitle}</h4>
        {resolvedDescription && (
          <p className="text-muted">{resolvedDescription}</p>
        )}
      </div>
      <CustomForm
        onSubmit={() => {}}
        defaultValues={defaultValues}
        values={defaultValues}
        shouldUnregister={true}>
        <FormValuesWatcher
          recordFields={recordFields}
          onChange={onChange}
          inputsMounted={recordFields.length > 0}
        />
        <div className="fields-section">
          <Stack
            gap={3}
            className="fields-container"
            style={{
              maxHeight: '15rem',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '0 0.25rem',
            }}>
            {recordFields.map((field) => (
              <div key={field.fieldInfo.name} className="mb-3">
                <FieldInput fieldInfo={field.fieldInfo} />
                <div className="text-muted small d-flex gap-2 ms-1 mt-n3">
                  <span>{t('current')}:</span>
                  <span>
                    <FormatFieldValue
                      name={field.fieldInfo.name}
                      value={field.value}
                      type={field.fieldInfo.type}
                      options={field.fieldInfo.options}
                    />
                  </span>
                </div>
              </div>
            ))}
          </Stack>
        </div>
      </CustomForm>
    </Stack>
  );
};
